import React, { useEffect, useState } from 'react';
import PromotionCardEditor from './PromotionCardEditor';
import PromotionTestPanel from './PromotionTestPanel';
import WhatsNewPromotionPanel from './WhatsNewPromotionPanel';
import {
  formatUkDateInput,
  parseUkDateInput,
  promotionChannelLabel,
  promotionLaunchInterrupted,
  promotionRecipientStatus,
  promotionSkipReasonLabel,
  ukDate,
  type PromotionDetail as PromotionDetailData,
} from './promotionTypes';

const WHATS_NEW_PROMOTION_SLUG = 'whats-new-carousel';

interface PromotionDetailProps {
  detail: PromotionDetailData;
  busy: string | null;
  onBack: () => void;
  onAction: (action: 'pause' | 'resume' | 'close') => void;
  onStart: () => void;
  onExtend: (endDate: string) => void;
  onSaveSettings: (settings: {
    startDate: string;
    endDate: string;
    weekdays: number[];
    partsOfDay: Array<'morning' | 'afternoon' | 'evening'>;
  }) => void;
  onRefreshCardStatus: () => void;
  auth: () => Promise<{ headers: { Authorization: string } }>;
  onCardSubmitted: () => Promise<void>;
}

const PromotionDetail: React.FC<PromotionDetailProps> = ({
  detail,
  busy,
  onBack,
  onAction,
  onStart,
  onExtend,
  onSaveSettings,
  onRefreshCardStatus,
  auth,
  onCardSubmitted,
}) => {
  const { promotion, funnel, recipients } = detail;
  const { definition } = promotion;
  const [endDateInput, setEndDateInput] = useState(
    formatUkDateInput(promotion.endDate),
  );
  useEffect(() => {
    setEndDateInput(formatUkDateInput(promotion.endDate));
  }, [promotion.endDate]);
  const extendedEndDate = parseUkDateInput(endDateInput);
  const canExtend =
    extendedEndDate !== null && extendedEndDate > promotion.endDate;
  const launchInterrupted = promotionLaunchInterrupted(promotion);
  const cardLocked = Boolean(
    detail.cardTemplates?.some(
      (template) => template.status !== 'not_submitted',
    ),
  );
  const funnelStats = {
    Audience: funnel.audience,
    Holdout: funnel.holdout,
    Scheduled: funnel.scheduled,
    Sent: funnel.sent,
    Skipped: Object.values(funnel.skipped).reduce(
      (sum, count) => sum + count,
      0,
    ),
    Failed: funnel.failed,
    Clicked: funnel.clicked,
    Adopted: funnel.adopted,
  };

  return (
    <section className='launch-detail promotion-detail'>
      <header className='launch-header promotion-detail__header'>
        <div>
          <button
            type='button'
            className='promotion-back-link'
            onClick={onBack}
          >
            ← Promotions
          </button>
          <h3>
            {promotion.name}
            <span
              className={`launch-status launch-status--${promotion.status}`}
            >
              {promotion.status === 'closed'
                ? 'Ended'
                : promotion.status === 'paused'
                  ? 'Paused'
                  : promotion.status === 'draft'
                    ? 'Draft'
                    : promotion.status === 'launching'
                      ? 'Launching…'
                      : 'Live'}
            </span>
          </h3>
          <p className='launch-description'>
            {ukDate(promotion.startDate)} – {ukDate(promotion.endDate)}
          </p>
          {promotion.status === 'launching' && (
            <p className='launch-hint' role='status'>
              {launchInterrupted
                ? "This launch didn't finish. Start it again to schedule the rest."
                : 'Choosing the audience and scheduling sends. This can take a few minutes.'}
            </p>
          )}
        </div>
        <div className='launch-status-actions'>
          {launchInterrupted && (
            <button
              type='button'
              className='launch-btn launch-btn--primary'
              disabled={busy !== null}
              onClick={onStart}
            >
              {busy === 'launch' ? 'Starting…' : 'Start again'}
            </button>
          )}
          {promotion.status === 'live' && (
            <button
              type='button'
              className='launch-btn launch-btn--quiet'
              disabled={busy !== null}
              onClick={() => onAction('pause')}
            >
              {busy === 'pause' ? 'Pausing…' : 'Pause'}
            </button>
          )}
          {promotion.status === 'paused' && (
            <button
              type='button'
              className='launch-btn'
              disabled={busy !== null}
              onClick={() => onAction('resume')}
            >
              {busy === 'resume' ? 'Resuming…' : 'Resume'}
            </button>
          )}
          {(promotion.status === 'live' || promotion.status === 'paused') && (
            <button
              type='button'
              className='launch-btn launch-btn--quiet'
              disabled={busy !== null}
              onClick={() => onAction('close')}
            >
              {busy === 'close' ? 'Closing…' : 'Close'}
            </button>
          )}
        </div>
      </header>

      {(promotion.status === 'live' || promotion.status === 'paused') && (
        <form
          className='launch-card promotion-extend'
          onSubmit={(event) => {
            event.preventDefault();
            if (canExtend && extendedEndDate) onExtend(extendedEndDate);
          }}
        >
          <div className='promotion-extend__row'>
            <label>
              End date
              <input
                value={endDateInput}
                placeholder='dd/mm/yyyy'
                inputMode='numeric'
                disabled={busy !== null}
                onChange={(event) => setEndDateInput(event.target.value)}
              />
            </label>
            <button
              type='submit'
              className='launch-btn'
              disabled={!canExtend || busy !== null}
            >
              {busy === 'extend' ? 'Extending…' : 'Extend'}
            </button>
          </div>
          <p className='launch-hint'>
            {extendedEndDate !== null &&
            extendedEndDate !== promotion.endDate &&
            !canExtend
              ? `Pick a date after ${ukDate(promotion.endDate)}.`
              : 'People skipped for the 14-day limit or for running out of time are rescheduled up to the new date.'}
          </p>
        </form>
      )}

      {promotion.codeDefined && (
        <WhatsNewPromotionPanel
          detail={detail}
          busy={busy}
          onRefresh={onRefreshCardStatus}
          onStart={onStart}
          onSave={onSaveSettings}
        />
      )}

      {promotion.status !== 'closed' && (
        <PromotionTestPanel
          slug={promotion.slug}
          definition={definition}
          cardApproved={Boolean(
            detail.cardTemplates?.some(
              (template) =>
                template.language === 'en' && template.status === 'approved',
            ),
          )}
          auth={auth}
          fixedContent={promotion.slug === WHATS_NEW_PROMOTION_SLUG}
        />
      )}

      {definition.card && (
        <PromotionCardEditor
          card={definition.card}
          onChange={() => undefined}
          slug={promotion.slug}
          editable={false}
          locked={cardLocked}
          canSubmit={false}
          approvals={detail.cardTemplates ?? []}
          auth={auth}
          onSubmitted={onCardSubmitted}
        />
      )}

      {promotion.codeDefined &&
        promotion.status !== 'draft' &&
        detail.sectionClicks && (
          <section className='launch-card'>
            <div className='launch-card-header'>
              <h4>Clicks by card</h4>
            </div>
            <dl>
              {[
                ['intro', 'Intro'],
                ['tasks', 'Tasks'],
                ['drafting', 'Drafting'],
                ['inbox', 'Inbox'],
                ['scheduling', 'Scheduling'],
                ['cc', 'CC'],
                ['expenses', 'Expenses'],
                ['travel', 'Travel'],
              ].map(([sectionId, label]) => (
                <div key={sectionId}>
                  <dt>{label}</dt>
                  <dd>{detail.sectionClicks?.[sectionId] ?? 0}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

      <div className='promotion-channel-stats'>
        {promotion.channels.map((channel) => {
          const channelCounts = funnel.channelStats[channel];
          return (
            <div className='launch-card' key={channel}>
              <div className='launch-card-header'>
                <h4>{promotionChannelLabel(channel)}</h4>
              </div>
              <dl className='promotion-channel-stats__values'>
                <div>
                  <dt>Sent</dt>
                  <dd>{channelCounts.sent}</dd>
                </div>
                <div>
                  <dt>Interested</dt>
                  <dd>{channelCounts.interested}</dd>
                </div>
                <div>
                  <dt>Declined</dt>
                  <dd>{channelCounts.declined}</dd>
                </div>
              </dl>
            </div>
          );
        })}
      </div>

      <div className='promotion-funnel'>
        {Object.entries(funnelStats).map(([label, count]) => (
          <div className='launch-card' key={label}>
            <span>{label}</span>
            <strong>{count}</strong>
          </div>
        ))}
      </div>

      <div className='launch-card'>
        <div className='launch-card-header'>
          <h4>Outcomes</h4>
        </div>
        <div className='promotion-outcome-sections'>
          <section>
            <h5>Replies</h5>
            <p>
              {Object.entries(funnel.outcomes)
                .filter(
                  ([outcome, count]) => outcome !== 'link_clicked' && count > 0,
                )
                .map(
                  ([outcome, count]) =>
                    `${outcome.replace(/_/g, ' ')}: ${count}`,
                )
                .join(' · ') || 'No replies yet'}
            </p>
          </section>
          <section>
            <h5>Skipped</h5>
            <p>
              {Object.entries(funnel.skipped)
                .filter(([, count]) => count > 0)
                .map(
                  ([reason, count]) =>
                    `${promotionSkipReasonLabel(reason)}: ${count}`,
                )
                .join(' · ') || 'No skipped recipients'}
            </p>
          </section>
        </div>
      </div>

      <div className='launch-card promotion-recipients'>
        <div className='launch-card-header'>
          <h4>Recipients</h4>
        </div>
        <div className='promotion-table-wrap'>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Scheduled</th>
                <th>Sent</th>
                <th>Outcome</th>
                <th>Link clicked</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((recipient) => (
                <tr key={recipient.id}>
                  <td>{recipient.name || recipient.userId}</td>
                  <td>
                    {recipient.channel
                      ? promotionChannelLabel(recipient.channel)
                      : '—'}
                  </td>
                  <td>
                    {promotionRecipientStatus(
                      recipient.status,
                      recipient.skipReason,
                    )}
                    {recipient.format && (
                      <span className='promotion-recipient-format'>
                        {' '}
                        · {recipient.format === 'card' ? 'Card' : 'Message'}
                      </span>
                    )}
                  </td>
                  <td>{ukDate(recipient.scheduledFor)}</td>
                  <td>{ukDate(recipient.sentAt)}</td>
                  <td>
                    {recipient.outcome === 'none' ||
                    recipient.outcome === 'pending'
                      ? '—'
                      : recipient.outcome.replace(/_/g, ' ')}
                  </td>
                  <td>{ukDate(recipient.linkClickedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default PromotionDetail;
